import React, { useEffect, useMemo, useRef, useState } from "react";
import { searchMints } from "../api/nft";
import { useTranslation } from "react-i18next";

const MIN_QUERY_LENGTH = 1;
const SEARCH_DEBOUNCE_MS = 220;

export function MintPicker({
  id,
  value,
  selectedMint,
  gradingSystem,
  countryRefCode,
  year,
  onChangeValue,
  onMintSelect,
  onClearMint,
  onBlur,
  disabled = false,
  placeholder,
  className = "cert-input",
}) {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [loadError, setLoadError] = useState("");
  const rootRef = useRef(null);
  const activeQuery = useMemo(() => String(value || "").trim(), [value]);
  const normalizedYear = useMemo(() => {
    const parsed = Number(String(year ?? "").trim());
    return Number.isInteger(parsed) ? parsed : undefined;
  }, [year]);

  useEffect(() => {
    const handlePointerDown = (event) => {
      if (!rootRef.current) return;
      if (rootRef.current.contains(event.target)) return;
      setIsOpen(false);
    };
    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, []);

  useEffect(() => {
    if (disabled) {
      setItems([]);
      setIsOpen(false);
      setIsLoading(false);
      setLoadError("");
      return undefined;
    }
    if (!isOpen) {
      return undefined;
    }
    if (!activeQuery || activeQuery.length < MIN_QUERY_LENGTH) {
      setItems([]);
      setLoadError("");
      setIsLoading(false);
      return undefined;
    }

    let isMounted = true;
    const timer = setTimeout(async () => {
      setIsLoading(true);
      setLoadError("");
      try {
        const response = await searchMints({
          query: activeQuery,
          gradingSystem,
          countryRefCode,
          year: normalizedYear,
          limit: 20,
        });
        if (!isMounted) return;
        const nextItems = Array.isArray(response?.data?.items) ? response.data.items : [];
        setItems(nextItems);
      } catch (error) {
        if (!isMounted) return;
        setItems([]);
        setLoadError(t("cert.fields.mint.searchError", { defaultValue: "Unable to load options." }));
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      isMounted = false;
      clearTimeout(timer);
    };
  }, [activeQuery, countryRefCode, disabled, gradingSystem, isOpen, normalizedYear, t]);

  const handleInputChange = (event) => {
    const nextValue = event.target.value;
    onChangeValue(nextValue);
    if (selectedMint?.code && nextValue !== (selectedMint.display_name || "")) {
      handleClearSelection();
    }
    setIsOpen(true);
    setLoadError("");
  };

  const handleSelect = (item) => {
    onChangeValue(item?.display_name || "");
    onMintSelect(item);
    setItems([]);
    setLoadError("");
    setIsOpen(false);
  };

  const handleClearSelection = () => {
    if (typeof onClearMint === "function") {
      onClearMint();
      return;
    }
    onMintSelect(null);
  };

  const renderOptionMeta = (item) => {
    const shortCode = String(item?.short_code || "").trim();
    const period = String(item?.period || "").trim();
    if (shortCode && period) return `${shortCode} · ${period}`;
    if (shortCode) return shortCode;
    return period;
  };

  const renderMenu = () => {
    if (!isOpen || disabled) return null;
    if (!activeQuery || activeQuery.length < MIN_QUERY_LENGTH) {
      return (
        <div className="mint-picker__menu mint-picker__menu--hint">
          {t("cert.fields.mint.searchMinChars", {
            defaultValue: "Type at least {{count}} characters",
            count: MIN_QUERY_LENGTH,
          })}
        </div>
      );
    }
    if (isLoading) {
      return (
        <div className="mint-picker__menu mint-picker__menu--hint">
          {t("cert.fields.mint.searchLoading", { defaultValue: "Searching..." })}
        </div>
      );
    }
    if (loadError) {
      return <div className="mint-picker__menu mint-picker__menu--error">{loadError}</div>;
    }
    if (!items.length) {
      return (
        <div className="mint-picker__menu mint-picker__menu--hint">
          {t("cert.fields.mint.searchEmpty", {
            defaultValue: "No matches. Free text will still be saved.",
          })}
        </div>
      );
    }
    return (
      <ul className="mint-picker__menu mint-picker__menu--list" role="listbox">
        {items.map((item) => {
          const code = String(item.code || "");
          const isSelected = code && selectedMint?.code === code;
          return (
            <li key={code}>
              <button
                type="button"
                className={`mint-picker__option ${isSelected ? "mint-picker__option--selected" : ""}`}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => handleSelect(item)}
                data-testid="mint-option"
              >
                <span className="mint-picker__name">{item.display_name}</span>
                <span className="mint-picker__meta">{renderOptionMeta(item)}</span>
              </button>
            </li>
          );
        })}
      </ul>
    );
  };

  return (
    <div className="mint-picker" ref={rootRef}>
      <input
        id={id}
        value={value}
        onChange={handleInputChange}
        onFocus={() => setIsOpen(true)}
        onBlur={onBlur}
        type="text"
        placeholder={placeholder || t("cert.fields.mint.placeholder", { defaultValue: "Search and select" })}
        className={className}
        autoComplete="off"
        aria-autocomplete="list"
        aria-expanded={isOpen}
        disabled={disabled}
        maxLength={64}
      />
      {renderMenu()}
    </div>
  );
}
